import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Skeleton, alpha, Avatar, Chip, TextField, MenuItem
} from '@mui/material';
import { Assessment, TrendingUp, MonetizationOn, People, BarChart } from '@mui/icons-material';
import { reportsAPI } from '../../services/api';

const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

const TabPanel = ({ children, value, index }) => value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

const Reports = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [collections, setCollections] = useState([]);
  const [providerReport, setProviderReport] = useState([]);
  const [contractorReport, setContractorReport] = useState([]);
  const [profitReport, setProfitReport] = useState([]);
  const [period, setPeriod] = useState('monthly');

  useEffect(() => { document.title = 'Reports | MyPayPlus'; }, []);
  useEffect(() => {
    const load = async () => {
      try {
        const [d, c, p, ct, pr] = await Promise.all([
          reportsAPI.dashboard().catch(() => ({ data: null })),
          reportsAPI.collections({ period }).catch(() => ({ data: [] })),
          reportsAPI.providerBilling({ period }).catch(() => ({ data: [] })),
          reportsAPI.contractorBilling({ period }).catch(() => ({ data: [] })),
          reportsAPI.profitability({ period }).catch(() => ({ data: [] }))
        ]);
        setDashboard(d.data); setCollections(Array.isArray(c.data) ? c.data : c.data?.data || []);
        setProviderReport(Array.isArray(p.data) ? p.data : p.data?.data || []); setContractorReport(Array.isArray(ct.data) ? ct.data : ct.data?.data || []);
        setProfitReport(Array.isArray(pr.data) ? pr.data : pr.data?.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [period]);

  if (loading) return <Box>{[...Array(3)].map((_, i) => <Skeleton key={i} height={200} sx={{ mb: 2, borderRadius: 3 }} />)}</Box>;

  const SummaryCard = ({ title, value, icon, gradient }) => (
    <Card sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } }}>
      <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, '&:last-child': { pb: 2.5 } }}>
        <Avatar sx={{ width: 44, height: 44, borderRadius: 2.5, background: gradient }}>
          {icon}
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>{title}</Typography>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 800 }}>{value}</Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Reports</Typography>
          <Typography variant="body2" color="text.secondary">Analytics and business intelligence</Typography>
        </Box>
        <TextField size="small" select value={period} onChange={e => { setPeriod(e.target.value); setLoading(true); }}
          sx={{ width: 160 }}>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="quarterly">Quarterly</MenuItem>
          <MenuItem value="yearly">Yearly</MenuItem>
        </TextField>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 3, pt: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
            '& .MuiTabs-indicator': { bgcolor: '#3b82f6', height: 3, borderRadius: 1.5 } }}>
          <Tab icon={<Assessment sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
          <Tab icon={<MonetizationOn sx={{ fontSize: 18 }} />} iconPosition="start" label="Collections" />
          <Tab icon={<TrendingUp sx={{ fontSize: 18 }} />} iconPosition="start" label="Provider Billing" />
          <Tab icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" label="Contractor Billing" />
          <Tab icon={<BarChart sx={{ fontSize: 18 }} />} iconPosition="start" label="Profitability" />
        </Tabs>
      </Card>

      <TabPanel value={tab} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <SummaryCard title="Total Collected" value={fmt(dashboard?.collections?.monthlyCollected)}
              icon={<MonetizationOn sx={{ fontSize: 22 }} />} gradient="linear-gradient(135deg, #3b82f6, #2563eb)" />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard title="Total Billed" value={fmt(dashboard?.collections?.monthlyBilled)}
              icon={<Assessment sx={{ fontSize: 22 }} />} gradient="linear-gradient(135deg, #f59e0b, #d97706)" />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard title="Total Profit" value={fmt(dashboard?.collections?.monthlyProfit)}
              icon={<TrendingUp sx={{ fontSize: 22 }} />} gradient="linear-gradient(135deg, #10b981, #059669)" />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard title="Active Providers" value={dashboard?.providers?.active || 0}
              icon={<People sx={{ fontSize: 22 }} />} gradient="linear-gradient(135deg, #8b5cf6, #7c3aed)" />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Collections</TableCell>
                  <TableCell align="right">Billed</TableCell>
                  <TableCell align="right">Commission</TableCell>
                  <TableCell align="right">Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collections.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No collection data available</Typography>
                  </TableCell></TableRow>
                ) : collections.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r._id || r.period || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#3b82f6' }}>{fmt(r.totalCollected)}</TableCell>
                    <TableCell align="right">{fmt(r.totalBilled)}</TableCell>
                    <TableCell align="right">{fmt(r.totalCommission)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#059669' }}>{fmt(r.totalProfit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Collections</TableCell>
                  <TableCell align="right">Billed</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Invoices</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providerReport.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No provider billing data</Typography>
                  </TableCell></TableRow>
                ) : providerReport.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.providerName || r._id || '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#3b82f6' }}>{fmt(r.totalCollected)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.totalBilled)}</TableCell>
                    <TableCell align="right">{r.billingRate ? `${r.billingRate}%` : '—'}</TableCell>
                    <TableCell align="right"><Chip label={r.invoiceCount || 0} size="small" sx={{ fontWeight: 700, height: 22, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contractor</TableCell>
                  <TableCell align="right">Commission Earned</TableCell>
                  <TableCell align="right">Invoiced</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contractorReport.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No contractor billing data</Typography>
                  </TableCell></TableRow>
                ) : contractorReport.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.contractorName || r._id || '—'}</TableCell>
                    <TableCell align="right" sx={{ color: '#8b5cf6', fontWeight: 600 }}>{fmt(r.totalCommission)}</TableCell>
                    <TableCell align="right">{fmt(r.totalInvoiced)}</TableCell>
                    <TableCell align="right" sx={{ color: '#059669' }}>{fmt(r.totalPaid)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: r.balance > 0 ? '#dc2626' : '#059669' }}>{fmt(r.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={4}>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Costs</TableCell>
                  <TableCell align="right">Profit</TableCell>
                  <TableCell align="right">Margin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profitReport.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No profitability data</Typography>
                  </TableCell></TableRow>
                ) : profitReport.map((r, i) => {
                  const margin = r.revenue > 0 ? Math.round((r.profit / r.revenue) * 100) : 0;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.providerName || r._id || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: '#3b82f6' }}>{fmt(r.revenue)}</TableCell>
                      <TableCell align="right">{fmt(r.costs)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#059669' }}>{fmt(r.profit)}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${margin}%`} size="small" sx={{
                          fontWeight: 700, height: 24,
                          bgcolor: margin >= 30 ? alpha('#10b981', 0.1) : margin >= 15 ? alpha('#f59e0b', 0.1) : alpha('#ef4444', 0.1),
                          color: margin >= 30 ? '#059669' : margin >= 15 ? '#d97706' : '#dc2626'
                        }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default Reports;
